import { DOCUMENT } from '@angular/common';
import { Inject, Injectable } from '@angular/core';
import { Meta, Title } from '@angular/platform-browser';
import { NavigationStart, Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { Observable, of } from 'rxjs';
import { catchError, map, take } from 'rxjs/operators';
import { environment, ENV_BUILD } from '../../../environments/environment';
import { GroupsService } from '../../groups/groups.service';
import { PageTitleEnum } from '../enums/page-title.enum';
import { AuthService, AuthType } from './auth.service';

export enum CustomMetaTagEnum {
  IS_CORRECT_RENDER = 'is_correct_render'
}

@Injectable({
  providedIn: 'root'
})
export class MetaService {

  isDefaultMetaGet: boolean = false;
  // Used to avoid overriding page level (e.g. topic) metadata with tab/group/default metadata in the same navigation
  private navigationId: number = 0;
  private pageMetaDataNavigationId: number = null;
  defaultTitle: string;
  defaultDescription: string;
  defaultImageUrl: string;
  constructor(
    private meta: Meta,
    private titleService: Title,
    private authService: AuthService,
    private groupsService: GroupsService,
    private translate: TranslateService,
    private router: Router,
    @Inject(DOCUMENT) private document: Document
  ) {
    this.router.events.subscribe(event => {
      if (event instanceof NavigationStart) {
        this.navigationId = event.id;
      }
    });
  }

  // Page level metadata (e.g. topic) has priority over tab/group metadata for the current navigation
  setPageMetaData(metaData: { title: string, description: string, imageUrl: string }): void {
    this.pageMetaDataNavigationId = this.navigationId;
    this.setMetaData(metaData);
  }

  hasPageMetaData(): boolean {
    return this.pageMetaDataNavigationId === this.navigationId;
  }

  // Group level metadata from App Appearance addon, Arabic values are used when current language is Arabic
  getGroupMetaData(): Observable<{ metaTitle: string, metaDescription: string }> {
    if (!this.authService.idenediGroupId) {
      return of({ metaTitle: null, metaDescription: null });
    }
    return this.authService.getAddonsAuth(this.authService.idenediGroupId).pipe(
      take(1),
      map(addons => {
        const props = addons?.find(addon => addon.Type === AuthType.AppAppearance)?.PublicProperties;
        const isArabic = this.translate.currentLang === 'ar';
        const metaTitle = (isArabic && props?.groupMetaTitleArabic) || props?.groupMetaTitle || null;
        const metaDescription = (isArabic && props?.groupMetaDescriptionArabic) || props?.groupMetaDescription || null;
        return { metaTitle, metaDescription };
      }),
      catchError(() => of({ metaTitle: null, metaDescription: null }))
    );
  }
  setDefaultMetaData() {
    if (environment.build == ENV_BUILD.STEMEXE) {      //if it's stemexe build
      if (environment.enableGroupsSelection) {//set default stemxe title if group selection is enabled
        this.titleService.setTitle(PageTitleEnum.StemexeTitle);
        this.document.getElementById('appFavicon').setAttribute('href', 'assets/stemexe-fav.png');
        //
      } else if (this.authService.idenediGroupId) {
        this.groupsService.getUserGroupsProfiles([this.authService.idenediGroupId])
         .subscribe(groupInfo => {
            if (groupInfo.FirstName && groupInfo.FirstName != '') {
              this.titleService.setTitle(`${groupInfo.FirstName} | StemeXe`);
            } else {
              this.titleService.setTitle(PageTitleEnum.StemexeTitle);
            }
            const smallProfileImageUrl = groupInfo.ProfileImageUrl?.replace('/profileimages/', '/profileimages-small/');

            const favicon = smallProfileImageUrl ?? 'assets/stemexe-fav.png';
            this.document.getElementById('appFavicon').setAttribute('href', favicon);

          })
      } else {
        this.titleService.setTitle(PageTitleEnum.StemexeTitle);


      }
    } else if (environment.build == ENV_BUILD.EXCEEDERS) {
      this.titleService.setTitle(PageTitleEnum.ExceedersTitle);
      this.document.getElementById('appFavicon').setAttribute('href', 'assets/exceeders-fav.jpg');
    } else {
      this.titleService.setTitle(PageTitleEnum.StemexeTitle);
      this.document.getElementById('appFavicon').setAttribute('href', 'assets/stemexe-fav.png');

    }
  }
  setTitle(title) {
    this.titleService.setTitle(title);
  }

  _setDefaultMetaData() {
    this.defaultTitle = this.meta.getTag("property='title'")?.content;
    this.defaultDescription = this.meta.getTag("name='description'")?.content;
    this.defaultImageUrl = `${environment.webHost}/assets/images/meta-preview-img.png`;
  }

  setMetaData(metaData: {
    title: string,
    description: string,
    imageUrl: string
  }): void {

    const title = metaData?.title || this.defaultTitle;
    const description = metaData?.description || this.defaultDescription;
    const imageUrl = metaData?.imageUrl || this.defaultImageUrl;

    this.setTag({
      name: 'twitter:card',
      content: 'summary'
    }, "name='twitter:card'")

    this.setTag({
      name: 'twitter:title',
      content: title
    }, "name='twitter:title'")

    this.setTag({
      name: 'twitter:description',
      content: description
    }, "name='twitter:description'")

    this.setTag({
      name: 'twitter:image',
      content: imageUrl
    }, "name='twitter:image'")

    this.setTag({
      prefix: 'og: http://ogp.me/ns#',
      name: 'og:type',
      content: 'article'
    }, "name='og:type'")


    this.setTag({
      prefix: 'og: http://ogp.me/ns#',
      property: 'og:title',
      content: title
    }, "property='og:title'")

    this.setTag({
      prefix: 'og: http://ogp.me/ns#',
      name: 'og:image:url',
      content: imageUrl
    }, "name='og:image:url'")

    this.setTag({
      prefix: 'og: http://ogp.me/ns#',
      property: 'og:title',
      content: title
    }, "property='og:title'")

    this.setTag({
      prefix: 'og: http://ogp.me/ns#',
      property: 'og:description',
      content: description
    }, "property='og:description'")


    this.setTag({
      prefix: 'og: http://ogp.me/ns#',
      property: 'og:image',
      content: imageUrl
    }, "property='og:image'")

    this.setTag({
      name: 'robots',
      content: 'index'
    }, "name='robots'")

    this.setTag({
      property: 'title',
      content: title
    }, "property='title'")

    this.setTag({
      name: 'description',
      content: description
    }, "name='description'")

    this.setTag({
      property: 'og:title',
      content: title
    }, "property='og:title'")

    this.setTag({
      property: 'og:title',
      content: title
    }, "property='og:title'")

    this.setTag({
      property: 'og:description',
      content: description
    }, "property='og:description'")

    this.setTag({
      property: 'image',
      content: imageUrl
    }, "property='image'")

  }

  setTag(tag, attribute) {

    if (this.meta.getTag(attribute)) {
      this.meta.updateTag(tag);
    } else {
      this.meta.addTag(tag);
    }

  }

  setCustomMetaTagValue(customTagName: CustomMetaTagEnum, value: 0 | 1): void {
    if (this.meta.getTag(customTagName)) {
      this.meta.updateTag({
        property: customTagName,
        content: value + ''
      });
    }
    this.meta.addTag({ property: customTagName, content: value + '' })
  }

  removeCustomMetaTag(customTagName: CustomMetaTagEnum): void {
    this.meta.removeTag(customTagName)
  }

  setCanonicalURL(path?: string) {
    let url;
    if (path) {
      url = (new URL(this.document.URL)).origin + path;
    }

    let link: HTMLLinkElement = this.document.querySelector(`link[rel='canonical']`) || null;
    if (!link) {
      link = this.document.createElement('link') as HTMLLinkElement;
      this.document.head.appendChild(link);
    }
    link.setAttribute('rel', 'canonical')
    link.setAttribute('href', url ?? this.document.URL)
  }


  removeCanonicalURL() {
    let link: HTMLLinkElement = this.document.querySelector(`link[rel='canonical']`) || null;
    link?.remove()
  }


}
